import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Pencil, Plus, Star, Trash2, X } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";

const STATUSES = ["Concluídos", "Em Desenvolvimento", "Em Construção", "Reabilitação", "Construção Nova", "Vendido em Projeto"];

const EMPTY_FORM = {
  name: "",
  location: "",
  year: "",
  typology: "",
  status: "Em Desenvolvimento",
  status_detail: "",
  credit_pt: "",
  credit_en: "",
  featured: false,
  for_sale: false,
  image: "",
  area: "",
  description_pt: "",
  description_en: "",
  context_pt: "",
  context_en: "",
  strategy_pt: "",
  strategy_en: "",
  value_pt: "",
  value_en: "",
  galleryText: "",
};

const inputCls =
  "w-full rounded-md border border-white/10 bg-basalt px-3.5 py-2.5 text-sm text-sand outline-none transition-colors focus:border-terracotta";

const Field = ({ label, children }) => (
  <label className="block">
    <span className="mb-1.5 block font-mono text-[11px] uppercase tracking-widest text-mist">{label}</span>
    {children}
  </label>
);

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [projects, setProjects] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);
  const [pendingReviews, setPendingReviews] = useState(0);

  const loadProjects = useCallback(() => {
    api.get("/projects").then((r) => setProjects(r.data)).catch(() => {});
    api
      .get("/reviews/all")
      .then((r) => setPendingReviews(r.data.filter((x) => x.status === "Pendente").length))
      .catch(() => {});
  }, []);

  useEffect(() => {
    api
      .get("/auth/me")
      .then((r) => {
        setUser(r.data);
        loadProjects();
      })
      .catch(() => navigate("/admin"));
  }, [navigate, loadProjects]);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setFormError("");
    setModalOpen(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({
      ...EMPTY_FORM,
      ...p,
      galleryText: (p.gallery || []).join("\n"),
    });
    setFormError("");
    setModalOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setFormError("");
    setSaving(true);
    const { galleryText, ...rest } = form;
    const payload = {
      ...rest,
      gallery: galleryText.split("\n").map((s) => s.trim()).filter(Boolean),
    };
    try {
      if (editing) {
        await api.put(`/projects/${editing.id}`, payload);
      } else {
        await api.post("/projects", payload);
      }
      setModalOpen(false);
      loadProjects();
    } catch (err) {
      if (err.response?.status === 401) {
        navigate("/admin");
        return;
      }
      setFormError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (p) => {
    if (!window.confirm(`Eliminar "${p.name}"?`)) return;
    try {
      await api.delete(`/projects/${p.id}`);
      loadProjects();
    } catch (err) {
      if (err.response?.status === 401) navigate("/admin");
    }
  };

  const handleLogout = async () => {
    await api.post("/auth/logout").catch(() => {});
    navigate("/admin");
  };

  if (!user) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-basalt" data-testid="admin-loading">
        <p className="font-mono text-xs tracking-widest text-mist">A verificar sessão…</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-basalt font-sans text-sand" data-testid="admin-dashboard">
      <header className="border-b border-white/10 bg-surface/60 backdrop-blur-md">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-5">
          <div>
            <p className="flex items-center">
              <img src="/logo.svg" alt="AZORUniqueHome" className="h-6 w-auto" />
              <span className="ml-3 font-mono text-[10px] uppercase tracking-[0.3em] text-mist">Admin</span>
            </p>
            <p className="mt-0.5 font-mono text-[11px] text-mist" data-testid="admin-user-email">{user.email}</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate("/admin/avaliacoes")}
              data-testid="admin-reviews-link"
              className="flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 font-mono text-[11px] tracking-widest text-sand/70 transition-colors hover:border-terracotta hover:text-terracotta"
            >
              <Star size={13} /> Avaliações
              {pendingReviews > 0 && (
                <span
                  data-testid="admin-pending-reviews-badge"
                  className="flex h-4 min-w-4 items-center justify-center rounded-full bg-terracotta px-1 text-[10px] font-semibold text-basalt"
                >
                  {pendingReviews}
                </span>
              )}
            </button>
            <button
              onClick={handleLogout}
              data-testid="admin-logout-button"
              className="flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 font-mono text-[11px] tracking-widest text-sand/70 transition-colors hover:border-terracotta hover:text-terracotta"
            >
              <LogOut size={13} /> Sair
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-6 py-10">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <h1 className="font-display text-2xl sm:text-3xl" data-testid="admin-projects-title">
            Projetos
            <span className="ml-3 align-middle font-mono text-sm tracking-widest text-mist">
              ({projects.length})
            </span>
          </h1>
          <button
            onClick={openCreate}
            data-testid="admin-new-project-button"
            className="flex items-center gap-2 rounded-full bg-terracotta px-5 py-2.5 text-sm font-medium text-basalt transition-colors hover:bg-sand"
          >
            <Plus size={15} /> Novo Projeto
          </button>
        </div>

        <div className="mt-8 space-y-3" data-testid="admin-projects-list">
          {projects.map((p) => (
            <div
              key={p.id}
              className="flex items-center gap-4 rounded-lg border border-white/10 bg-surface p-3.5"
              data-testid={`admin-project-row-${p.id}`}
            >
              {p.image ? (
                <img src={p.image} alt={p.name} className="h-14 w-20 shrink-0 rounded-md object-cover" />
              ) : (
                <div className="h-14 w-20 shrink-0 rounded-md bg-basalt" />
              )}
              <div className="min-w-0">
                <p className="truncate font-display text-base text-sand">{p.name}</p>
                <p className="truncate font-mono text-[11px] uppercase tracking-widest text-mist">
                  {p.location}
                  {p.year ? ` · ${p.year}` : ""}
                  {p.typology ? ` · ${p.typology}` : ""}
                </p>
              </div>
              <span className="ml-auto hidden shrink-0 rounded-full border border-white/15 px-3 py-1 font-mono text-[10px] uppercase tracking-widest text-sand/60 sm:block">
                {p.status}
              </span>
              <button
                onClick={() => openEdit(p)}
                data-testid={`project-edit-${p.id}`}
                className="shrink-0 rounded-md border border-white/15 p-2 text-sand/70 transition-colors hover:border-terracotta hover:text-terracotta"
                aria-label={`Editar ${p.name}`}
              >
                <Pencil size={14} />
              </button>
              <button
                onClick={() => handleDelete(p)}
                data-testid={`project-delete-${p.id}`}
                className="shrink-0 rounded-md border border-white/15 p-2 text-sand/70 transition-colors hover:border-red-500 hover:text-red-400"
                aria-label={`Eliminar ${p.name}`}
              >
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
      </main>

      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm" data-testid="project-form-modal">
          <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-xl border border-white/10 bg-surface p-7">
            <div className="mb-6 flex items-center justify-between">
              <h2 className="font-display text-xl" data-testid="project-form-title">
                {editing ? "Editar Projeto" : "Novo Projeto"}
              </h2>
              <button
                onClick={() => setModalOpen(false)}
                data-testid="project-form-close"
                className="text-mist transition-colors hover:text-sand"
                aria-label="Fechar"
              >
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleSave} className="grid gap-5 sm:grid-cols-2">
              <Field label="Nome *">
                <input
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className={inputCls}
                  data-testid="project-name-input"
                />
              </Field>
              <Field label="Localização">
                <input
                  value={form.location}
                  onChange={(e) => setForm({ ...form, location: e.target.value })}
                  className={inputCls}
                  data-testid="project-location-input"
                />
              </Field>
              <Field label="Ano">
                <input
                  value={form.year}
                  onChange={(e) => setForm({ ...form, year: e.target.value })}
                  className={inputCls}
                  data-testid="project-year-input"
                />
              </Field>
              <Field label="Tipologia">
                <input
                  value={form.typology}
                  onChange={(e) => setForm({ ...form, typology: e.target.value })}
                  className={inputCls}
                  data-testid="project-typology-input"
                />
              </Field>
              <Field label="Estado">
                <select
                  value={form.status}
                  onChange={(e) => setForm({ ...form, status: e.target.value })}
                  className={inputCls}
                  data-testid="project-status-select"
                >
                  {STATUSES.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </Field>
              <Field label="Detalhe do Estado (ex: Reabilitação)">
                <input
                  value={form.status_detail}
                  onChange={(e) => setForm({ ...form, status_detail: e.target.value })}
                  className={inputCls}
                  data-testid="project-status-detail-input"
                />
              </Field>
              <div className="flex flex-wrap items-center gap-6 self-end pb-1 sm:col-span-2">
                <label className="flex cursor-pointer items-center gap-2.5">
                  <input
                    type="checkbox"
                    checked={form.for_sale}
                    onChange={(e) => setForm({ ...form, for_sale: e.target.checked })}
                    className="h-4 w-4 accent-[#C86D51]"
                    data-testid="project-forsale-checkbox"
                  />
                  <span className="font-mono text-[11px] uppercase tracking-widest text-mist">Para venda</span>
                </label>
                <label className="flex cursor-pointer items-center gap-2.5">
                  <input
                    type="checkbox"
                    checked={form.featured}
                    onChange={(e) => setForm({ ...form, featured: e.target.checked })}
                    className="h-4 w-4 accent-[#C86D51]"
                    data-testid="project-featured-checkbox"
                  />
                  <span className="font-mono text-[11px] uppercase tracking-widest text-mist">Destaque na página inicial</span>
                </label>
              </div>
              <Field label="Área">
                <input
                  value={form.area}
                  onChange={(e) => setForm({ ...form, area: e.target.value })}
                  className={inputCls}
                  placeholder="ex: 320 m²"
                  data-testid="project-area-input"
                />
              </Field>
              <div className="sm:col-span-2">
                <Field label="URL da Imagem Principal">
                  <input
                    value={form.image}
                    onChange={(e) => setForm({ ...form, image: e.target.value })}
                    className={inputCls}
                    placeholder="https://…"
                    data-testid="project-image-input"
                  />
                </Field>
              </div>
              <div className="sm:col-span-2">
                <Field label="Galeria (URLs, uma por linha)">
                  <textarea
                    rows={3}
                    value={form.galleryText}
                    onChange={(e) => setForm({ ...form, galleryText: e.target.value })}
                    className={inputCls}
                    placeholder={"https://…\nhttps://…"}
                    data-testid="project-gallery-input"
                  />
                </Field>
              </div>
              <div className="sm:col-span-2">
                <Field label="Descrição (PT)">
                  <textarea
                    rows={3}
                    value={form.description_pt}
                    onChange={(e) => setForm({ ...form, description_pt: e.target.value })}
                    className={inputCls}
                    data-testid="project-descpt-input"
                  />
                </Field>
              </div>
              <div className="sm:col-span-2">
                <Field label="Descrição (EN)">
                  <textarea
                    rows={3}
                    value={form.description_en}
                    onChange={(e) => setForm({ ...form, description_en: e.target.value })}
                    className={inputCls}
                    data-testid="project-descen-input"
                  />
                </Field>
              </div>
              <Field label="Crédito de Arquitetura (PT)">
                <input
                  value={form.credit_pt}
                  onChange={(e) => setForm({ ...form, credit_pt: e.target.value })}
                  className={inputCls}
                  placeholder="Projeto de arquitetura desenvolvido pelo UAID Arquitetos"
                  data-testid="project-creditpt-input"
                />
              </Field>
              <Field label="Architecture Credit (EN)">
                <input
                  value={form.credit_en}
                  onChange={(e) => setForm({ ...form, credit_en: e.target.value })}
                  className={inputCls}
                  placeholder="Architecture project developed by UAID Arquitetos"
                  data-testid="project-crediten-input"
                />
              </Field>

              <p className="mt-2 border-t border-white/10 pt-5 font-mono text-[11px] uppercase tracking-[0.3em] text-terracotta sm:col-span-2">
                Capítulos da página de detalhe
              </p>
              <Field label="Contextualização (PT)">
                <textarea
                  rows={4}
                  value={form.context_pt}
                  onChange={(e) => setForm({ ...form, context_pt: e.target.value })}
                  className={inputCls}
                  data-testid="project-contextpt-input"
                />
              </Field>
              <Field label="Context (EN)">
                <textarea
                  rows={4}
                  value={form.context_en}
                  onChange={(e) => setForm({ ...form, context_en: e.target.value })}
                  className={inputCls}
                  data-testid="project-contexten-input"
                />
              </Field>
              <Field label="A estratégia (PT)">
                <textarea
                  rows={4}
                  value={form.strategy_pt}
                  onChange={(e) => setForm({ ...form, strategy_pt: e.target.value })}
                  className={inputCls}
                  data-testid="project-strategypt-input"
                />
              </Field>
              <Field label="The strategy (EN)">
                <textarea
                  rows={4}
                  value={form.strategy_en}
                  onChange={(e) => setForm({ ...form, strategy_en: e.target.value })}
                  className={inputCls}
                  data-testid="project-strategyen-input"
                />
              </Field>
              <Field label="O valor criado (PT)">
                <textarea
                  rows={4}
                  value={form.value_pt}
                  onChange={(e) => setForm({ ...form, value_pt: e.target.value })}
                  className={inputCls}
                  data-testid="project-valuept-input"
                />
              </Field>
              <Field label="The value created (EN)">
                <textarea
                  rows={4}
                  value={form.value_en}
                  onChange={(e) => setForm({ ...form, value_en: e.target.value })}
                  className={inputCls}
                  data-testid="project-valueen-input"
                />
              </Field>

              {formError && (
                <p className="rounded-md border border-terracotta/40 bg-terracotta/10 px-3 py-2 text-xs text-terracotta sm:col-span-2" data-testid="project-form-error">
                  {formError}
                </p>
              )}

              <div className="flex justify-end gap-3 sm:col-span-2">
                <button
                  type="button"
                  onClick={() => setModalOpen(false)}
                  data-testid="project-cancel-button"
                  className="rounded-full border border-white/15 px-5 py-2.5 text-sm text-sand/70 transition-colors hover:border-white/40"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  data-testid="project-save-button"
                  className="rounded-full bg-terracotta px-6 py-2.5 text-sm font-medium text-basalt transition-colors hover:bg-sand disabled:opacity-50"
                >
                  {saving ? "A guardar…" : "Guardar"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
